<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <DefineNav v-slot="{ collapsed }">
        <nav class="flex flex-col gap-1" :class="collapsed ? 'px-2 py-3' : 'p-3'">
            <UButton
                v-for="item in topItems"
                :key="item.name"
                :to="{ name: item.name }"
                :label="collapsed ? undefined : item.label"
                :title="item.label"
                :icon="item.icon"
                :color="isActive(item.name) ? 'primary' : 'neutral'"
                :variant="isActive(item.name) ? 'subtle' : 'ghost'"
                size="lg"
                :class="collapsed ? 'justify-center' : 'justify-start'"
                @click="closeMobileMenu"
            />

            <!-- The commercial side, folded like the CRM: what the house offers
                 before anything is booked. -->
            <template v-if="salesItems.length">
                <button
                    type="button"
                    class="mt-2 flex items-center gap-2 rounded py-2 text-left text-sm font-medium"
                    :class="[
                        salesOpen ? 'text-highlighted' : 'text-default hover:bg-elevated hover:text-highlighted',
                        collapsed ? 'justify-center px-0' : 'px-3',
                    ]"
                    :title="$t('nav.sales_group')"
                    :aria-expanded="salesOpen"
                    @click="toggleSales"
                >
                    <UIcon name="i-lucide-handshake" class="size-5 shrink-0" />
                    <span v-if="!collapsed" class="flex-1">{{ $t('nav.sales_group') }}</span>
                    <UIcon
                        v-if="!collapsed"
                        :name="salesOpen ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
                        class="size-4 shrink-0 text-dimmed"
                    />
                </button>

                <UButton
                    v-for="item in salesItems"
                    v-show="!collapsed && salesOpen"
                    :key="item.name"
                    :to="{ name: item.name }"
                    :label="collapsed ? undefined : item.label"
                    :title="item.label"
                    :icon="item.icon"
                    :color="isActive(item.name) ? 'primary' : 'neutral'"
                    :variant="isActive(item.name) ? 'subtle' : 'ghost'"
                    size="lg"
                    :class="collapsed ? 'justify-center' : 'justify-start ps-6'"
                    @click="closeMobileMenu"
                />
            </template>

            <!-- The relationship side of the house, folded under one heading: the
                 guests and the firms that pay for them, the segments, the funnel, the
                 to-do list and the overview that reads them all. It opens by itself
                 when the screen you are on is inside it. -->
            <template v-if="crmItems.length">
                <button
                    type="button"
                    class="mt-2 flex items-center gap-2 rounded py-2 text-left text-sm font-medium"
                    :class="[
                        crmOpen ? 'text-highlighted' : 'text-default hover:bg-elevated hover:text-highlighted',
                        collapsed ? 'justify-center px-0' : 'px-3',
                    ]"
                    :title="$t('nav.crm_group')"
                    :aria-expanded="crmOpen"
                    @click="toggleCrm"
                >
                    <UIcon name="i-lucide-heart-handshake" class="size-5 shrink-0" />
                    <span v-if="!collapsed" class="flex-1">{{ $t('nav.crm_group') }}</span>
                    <UIcon
                        v-if="!collapsed"
                        :name="crmOpen ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
                        class="size-4 shrink-0 text-dimmed"
                    />
                </button>

                <UButton
                    v-for="item in crmItems"
                    v-show="!collapsed && crmOpen"
                    :key="item.name"
                    :to="{ name: item.name }"
                    :label="collapsed ? undefined : item.label"
                    :title="item.label"
                    :icon="item.icon"
                    :color="isActive(item.name) ? 'primary' : 'neutral'"
                    :variant="isActive(item.name) ? 'subtle' : 'ghost'"
                    size="lg"
                    :class="collapsed ? 'justify-center' : 'justify-start ps-6'"
                    @click="closeMobileMenu"
                />
            </template>

            <UButton
                v-for="item in opsItems"
                :key="item.name"
                :to="{ name: item.name }"
                :label="collapsed ? undefined : item.label"
                :title="item.label"
                :icon="item.icon"
                :color="isActive(item.name) ? 'primary' : 'neutral'"
                :variant="isActive(item.name) ? 'subtle' : 'ghost'"
                size="lg"
                :class="collapsed ? 'justify-center' : 'justify-start'"
                @click="closeMobileMenu"
            />

            <!--
                MARKETING. Not a route of this portal but a doorway out of it: the
                module is its own application on its own subdomain
                (docs/MARKETING.md §2). It shows where the house HAS the module and
                the account may read it.

                The licence is asked as well as the permission, although either would
                do on its own today — marketing:* is masked when the module is off
                (pkg/enums/Module, permissionOwner). It is written out because this
                entry is not a NavItem and so is not filtered by `allow`, and the rule
                everywhere else in this menu is that the navigation is built from the
                licence.

                An ORDINARY LINK, and that is the whole point of it. It used to fetch
                a handoff and then window.open the answer, which every browser blocks:
                a window opened after an await is no longer attributable to the click
                that opened it, so the way into the module was a popup warning. The
                server mints the token and redirects (HTTP_API/Marketing.go), so the
                navigation IS the click and nothing has to be allowed.
            -->
            <UButton
                v-if="appStore.hasModule('marketing') && appStore.can('marketing:read')"
                :to="MARKETING_DOOR"
                target="_blank"
                rel="noopener"
                external
                icon="i-lucide-megaphone"
                :label="collapsed ? undefined : $t('nav.marketing')"
                :title="$t('nav.marketing')"
                :trailing-icon="collapsed ? undefined : 'i-lucide-external-link'"
                color="neutral"
                variant="ghost"
                size="lg"
                :class="collapsed ? 'justify-center' : 'justify-start'"
                @click="closeMobileMenu"
            />

            <!-- The store cupboard's own fold: materials, under its own heading. -->
            <template v-if="materialsItems.length">
                <button
                    type="button"
                    class="mt-2 flex items-center gap-2 rounded py-2 text-left text-sm font-medium"
                    :class="[
                        materialsOpen ? 'text-highlighted' : 'text-default hover:bg-elevated hover:text-highlighted',
                        collapsed ? 'justify-center px-0' : 'px-3',
                    ]"
                    :title="$t('nav.materials_group')"
                    :aria-expanded="materialsOpen"
                    data-testid="nav-materials-fold"
                    @click="toggleMaterials"
                >
                    <UIcon name="i-lucide-warehouse" class="size-5 shrink-0" />
                    <span v-if="!collapsed" class="flex-1">{{ $t('nav.materials_group') }}</span>
                    <UIcon
                        v-if="!collapsed"
                        :name="materialsOpen ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
                        class="size-4 shrink-0 text-dimmed"
                    />
                </button>

                <!-- Five quiet headings inside the one fold, exactly as the staff fold
                     below does it: nineteen entries in a flat list is a list nobody
                     reads down, and five sub-folds would be two clicks to a screen a
                     storekeeper opens all morning. A GROUP whose every entry was
                     filtered out draws no label either. -->
                <template v-for="group in materialsGroups" :key="group.key">
                    <!-- The heading and every entry carry a `data-testid` naming the
                         group and the ROUTE rather than the label: the browser suite
                         has to be able to say "the fifth fold is drawn and its entries
                         lead where they say", and three of the labels here are words
                         that also appear on the entries under them („Zaloga“,
                         „Prevzem“, „Veriga“). A test that matched on the text would
                         either be ambiguous or would go quietly green on a rename.
                         e2e/specs/01-navigation.spec.ts is the reader. -->
                    <!-- A group with no label draws no heading: „Pregled“ is the fold's
                         front door and stands above the five, not inside one of them. -->
                    <span
                        v-show="!collapsed && materialsOpen && group.label"
                        class="mt-2 pe-3 ps-6 text-xs font-medium uppercase tracking-wide text-dimmed"
                        :data-testid="`nav-materials-group-${group.key}`"
                    >
                        {{ group.label }}
                    </span>

                    <UButton
                        v-for="item in group.items"
                        v-show="!collapsed && materialsOpen"
                        :key="item.name"
                        :to="{ name: item.name }"
                        :data-testid="`nav-materials-item-${item.name}`"
                        :label="collapsed ? undefined : item.label"
                        :title="item.label"
                        :icon="item.icon"
                        :color="isActive(item.name) ? 'primary' : 'neutral'"
                        :variant="isActive(item.name) ? 'subtle' : 'ghost'"
                        size="lg"
                        :class="collapsed ? 'justify-center' : 'justify-start ps-6'"
                        @click="closeMobileMenu"
                    >
                        <!-- What is waiting behind the entry, where an entry has a number
                             to show. Nothing is drawn for zero: a badge saying „0“ is a
                             thing to read every morning that never means anything. -->
                        <template v-if="!collapsed && item.badge" #trailing>
                            <UBadge color="warning" variant="subtle" size="sm">{{ item.badge }}</UBadge>
                        </template>
                    </UButton>
                </template>
            </template>

            <!-- The people who work here, folded like the rest — but with three quiet
                 headings inside it (docs/OSEBJE-UI.md §2): what is MINE, what I LEAD and
                 the personnel registers. Ten entries in one flat list was a list nobody
                 could read; three sub-folds would have been two clicks to a week
                 somebody opens every day, so it is one fold with labels in it and no new
                 component.

                 The entries appear by permission — and because the API masks a disabled
                 module's permissions (CLAUDE.md deviation 15), a house that has not
                 bought `staff` sees no heading at all without this menu asking anything.
                 A GROUP with no entry left in it draws no label either. -->
            <template v-if="staffItems.length">
                <button
                    type="button"
                    class="mt-2 flex items-center gap-2 rounded py-2 text-left text-sm font-medium"
                    :class="[
                        staffOpen ? 'text-highlighted' : 'text-default hover:bg-elevated hover:text-highlighted',
                        collapsed ? 'justify-center px-0' : 'px-3',
                    ]"
                    :title="$t('nav.staff_group')"
                    :aria-expanded="staffOpen"
                    @click="toggleStaff"
                >
                    <UIcon name="i-lucide-users-round" class="size-5 shrink-0" />
                    <span v-if="!collapsed" class="flex-1">{{ $t('nav.staff_group') }}</span>
                    <UIcon
                        v-if="!collapsed"
                        :name="staffOpen ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
                        class="size-4 shrink-0 text-dimmed"
                    />
                </button>

                <template v-for="group in staffGroups" :key="group.key">
                    <span
                        v-show="!collapsed && staffOpen"
                        class="mt-2 pe-3 ps-6 text-xs font-medium uppercase tracking-wide text-dimmed"
                    >
                        {{ group.label }}
                    </span>

                    <UButton
                        v-for="item in group.items"
                        v-show="!collapsed && staffOpen"
                        :key="item.name"
                        :to="{ name: item.name }"
                        :label="collapsed ? undefined : item.label"
                        :title="item.label"
                        :icon="item.icon"
                        :color="isActive(item.name) ? 'primary' : 'neutral'"
                        :variant="isActive(item.name) ? 'subtle' : 'ghost'"
                        size="lg"
                        :class="collapsed ? 'justify-center' : 'justify-start ps-6'"
                        @click="closeMobileMenu"
                    />
                </template>
            </template>

            <!-- The things somebody sets up once and then leaves alone: the house
                 itself, the accounts, this browser's own look. They are folded away
                 because a desk opens them a few times a year and reads the eleven
                 entries above them all day.

                 It opens by itself when the screen you are on is inside it — a menu
                 that hides where you are is a menu you stop trusting — and otherwise
                 remembers whether it was left open on this machine. -->
            <template v-if="settingsItems.length">
                <button
                    type="button"
                    class="mt-2 flex items-center gap-2 rounded py-2 text-left text-sm font-medium"
                    :class="[
                        settingsOpen ? 'text-highlighted' : 'text-default hover:bg-elevated hover:text-highlighted',
                        collapsed ? 'justify-center px-0' : 'px-3',
                    ]"
                    :title="$t('nav.settings')"
                    :aria-expanded="settingsOpen"
                    @click="toggleSettings"
                >
                    <UIcon name="i-lucide-settings" class="size-5 shrink-0" />
                    <span v-if="!collapsed" class="flex-1">{{ $t('nav.settings') }}</span>
                    <UIcon
                        v-if="!collapsed"
                        :name="settingsOpen ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
                        class="size-4 shrink-0 text-dimmed"
                    />
                </button>

                <UButton
                    v-for="item in settingsItems"
                    v-show="!collapsed && settingsOpen"
                    :key="item.name"
                    :to="{ name: item.name }"
                    :label="collapsed ? undefined : item.label"
                    :title="item.label"
                    :icon="item.icon"
                    :color="isActive(item.name) ? 'primary' : 'neutral'"
                    :variant="isActive(item.name) ? 'subtle' : 'ghost'"
                    size="lg"
                    :class="collapsed ? 'justify-center' : 'justify-start ps-6'"
                    @click="closeMobileMenu"
                />
            </template>
        </nav>
    </DefineNav>

    <!--
        Minimised, this is a rail of icons rather than nothing at all. Hiding the menu
        outright would buy the same 200 px and cost the thing the menu is for: on a
        laptop at the desk, the chessboard and the arrivals are one press away all day,
        and a hidden menu makes them two — press to open, press to go. So the labels go
        and the icons stay, each one naming itself on hover.

        A fold has no rail form — there is no width for what hangs under it — so pressing
        one puts the menu back and opens it. See toggleSettings and its siblings.
    -->
    <aside
        class="hidden h-full shrink-0 border-e border-default bg-default lg:flex lg:flex-col"
        :class="collapsed ? 'w-16' : 'w-64'"
    >
        <div
            class="flex h-16 items-center gap-2 border-b border-default"
            :class="collapsed ? 'justify-center px-2' : 'px-5'"
        >
            <UIcon name="i-lucide-hotel" class="size-6 shrink-0 text-primary" />
            <div v-if="!collapsed" class="flex min-w-0 flex-col leading-tight">
                <span class="truncate text-base font-semibold text-highlighted">Imprion PMS</span>
                <AppAbout />
            </div>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto">
            <ReuseNav :collapsed="collapsed" />
        </div>

        <!-- At the bottom and not in the top bar: the control belongs to the thing it
             moves, and it is the one button on this screen that must not shift when it
             is pressed. -->
        <div class="border-t border-default p-2">
            <UButton
                type="button"
                :icon="collapsed ? 'i-lucide-panel-left-open' : 'i-lucide-panel-left-close'"
                :label="collapsed ? undefined : $t('nav.collapse_menu')"
                :title="collapsed ? $t('nav.expand_menu') : $t('nav.collapse_menu')"
                :aria-label="collapsed ? $t('nav.expand_menu') : $t('nav.collapse_menu')"
                :aria-expanded="!collapsed"
                color="neutral"
                variant="ghost"
                size="lg"
                class="w-full"
                :class="collapsed ? 'justify-center' : 'justify-start'"
                @click="toggleCollapsed"
            />
        </div>
    </aside>

    <USlideover
        v-model:open="mobileMenuOpen"
        :ui="{ content: 'w-72 max-w-[85vw]' }"
        title="Imprion PMS"
        side="left"
    >
        <template #body>
            <ReuseNav :collapsed="false" />
        </template>
    </USlideover>
</template>

<script lang="ts" setup>
    import { createReusableTemplate } from '@vueuse/core'
    import { computed, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute, useRouter } from 'vue-router'

    import AppAbout from '@/components/app/AppAbout.vue'
    import { useLayout } from '@/layouts/layout'
    import { listMyApprovals } from '@/libs/stockRequisition'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import type { ITelemetryDemoConsole } from '@/types/Telemetry'

    const { t }  = useI18n()
    const route  = useRoute()
    const router     = useRouter()
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const { mobileMenuOpen, closeMobileMenu } = useLayout()

    const COLLAPSED_KEY = 'pms:sidebar-collapsed'

    /**
     * Per machine and not per account: whether the menu is a rail is a fact about the
     * screen it is drawn on — a 13-inch laptop at the back office wants the width, the
     * 27-inch at the desk does not — and the same person moves between them.
     */
    const collapsed = ref(localStorage.getItem(COLLAPSED_KEY) === '1')

    const toggleCollapsed = () => {
        collapsed.value = !collapsed.value
        localStorage.setItem(COLLAPSED_KEY, collapsed.value ? '1' : '0')
    }

    /** Puts the menu back, and remembers that it is back. */
    const expandSidebar = () => {
        if (!collapsed.value) return

        collapsed.value = false
        localStorage.setItem(COLLAPSED_KEY, '0')
    }

    const [DefineNav, ReuseNav] = createReusableTemplate<{ collapsed: boolean }>()

    interface NavItem {
        /** Show when ANY of these keys is held — for a page that serves two roles
         *  with different tabs (Finance: the till and the invoices). */
        anyOf?: string[]
        /** Show only when EVERY one of these keys is held — for a screen whose FIRST
         *  reads need more than one of them. The till reads its own tills, the shelf
         *  behind them and its shift; the invoice register reads the supplier codebook
         *  before it reads an invoice; the chain screen asks the chain before it asks
         *  what the chain buys. It is the menu's half of the route's `meta.permissions`,
         *  and the two are written together — an entry that leads to a refusal, or to a
         *  guard that sends somebody home, is worse than no entry. */
        allOf?: string[]
        name:  string
        permission: string
        label: string
        icon:  string
        /** A screen that does NOT live under a house and is not on the service's
         *  short list of tenant-level reads. Those routes resolve permissions with no
         *  house named, so an account assigned to one house is refused however well its
         *  role is set up — and an entry that leads to a refusal is worse than no entry.
         *
         *  The register of houses is NOT one of these: the service widened that one
         *  read deliberately and answers it with the houses the person works in. The
         *  accounts are, until somebody decides what "the users of my hotel" means. */
        tenantWide?: boolean
        /** An extra condition, for an entry a permission alone cannot decide: the
         *  demo desk exists only where the house runs simulated equipment, and that is
         *  a property of the HOUSE rather than of the account. Absent means yes. */
        when?: boolean
        /** A number beside the entry — what is waiting behind it. Read ONCE when the
         *  menu is drawn and never polled: a count that ticks in the corner of the eye
         *  is a notification, and this portal already has one of those. Absent, or
         *  zero, draws nothing. */
        badge?: number
        /** The module the screen belongs to (docs/MODULI.md §3.3). An entry whose
         *  module this hotel has not bought is not drawn — not drawn greyed out, not
         *  drawn with an "upgrade" label beside it: the navigation is built from the
         *  licence. Absent means the core, which every hotel has. */
        module?: string
        /** Several licences at once, for the screen that stands on more than one — the
         *  transfers between houses of a chain need `materials` AND `chains`. Every one
         *  of them has to be held, and it is the menu's half of the same gate the route
         *  carries in `meta.modules`. */
        modules?: string[]
    }

    // Ordered the way a desk works, not the way the data is shaped: the guests and the
    // firms that pay for them stand together at the top, and the house itself — which
    // somebody opens when a room type changes, not when a guest is standing there —
    // sits down by the reports and the settings.
    //
    // Filtered against the router: an entry whose route does not exist yet would make
    // RouterLink throw on render, so a screen that has not landed simply stays out of
    // the menu until its route does.
    // A menu entry whose route has not landed, or that this account may not open,
    // is filtered out — a link that 403s teaches the desk the system is unreliable.
    const allow = (list: NavItem[]): NavItem[] => list
        .filter(item => router.hasRoute(item.name))
        .filter(item => item.when !== false)
        .filter(item => !item.module || appStore.hasModule(item.module))
        .filter(item => (item.modules ?? []).every(key => appStore.hasModule(key)))
        .filter(item => !item.tenantWide || appStore.everywhere)
        .filter(item => (item.allOf ?? []).every(permission => appStore.can(permission)))
        .filter(item => item.anyOf
            ? item.anyOf.some(permission => appStore.can(permission))
            : (!item.permission || appStore.can(item.permission)))

    // The reservations spine, above the folds.
    const topItems = computed((): NavItem[] => allow([
        // „Danes“ (v1.543.0, docs/DANES.md). The ONE addition this menu takes from the
        // redesign, and it is here because the sign-in now lands on it: a menu that did
        // not carry the screen somebody arrives on would be a menu with no way back to it.
        //
        // No permission, like its twin in the new menu: the page is drawn from what the
        // service answers, and an account that holds nothing it can speak about is told so
        // on the page rather than by an entry that is not there.
        { name: 'today',        label: t('nav.today'),        icon: 'i-lucide-sunrise',       permission: '' },
        { name: 'chessboard',   label: t('nav.chessboard'),   icon: 'i-lucide-layout-grid',   permission: 'reservation:read' },
        { name: 'availability', label: t('nav.availability'), icon: 'i-lucide-door-open',     permission: 'reservation:read' },
        { name: 'reservations', label: t('nav.reservations'), icon: 'i-lucide-calendar-days', permission: 'reservation:read' },
        { name: 'reminders',    label: t('nav.reminders'),    icon: 'i-lucide-bell',          permission: 'reservation:read' },
        { name: 'groups',       label: t('nav.groups'),       icon: 'i-lucide-users-round',   permission: 'reservation:read', module: 'groups_events' },
    ]))

    // The house and the money, below the CRM fold.
    /**
     * The door into the marketing portal: a real address, followed by a real click.
     *
     * The server behind it mints the short-lived handoff and redirects, so this portal
     * still does not know where the marketing one lives — that was the reason the old
     * arrangement asked first, and it is preserved. What is gone is the await between
     * the click and the window, which is what the browser was blocking.
     *
     * It authenticates by the session cookie, which a top-level navigation carries.
     */
    const MARKETING_DOOR = '/api/pms/v1/marketing/open'

    const opsItems = computed((): NavItem[] => allow([
        { name: 'rate-plans',   label: t('nav.rate_plans'),   icon: 'i-lucide-tags',          permission: 'rate:read' },
        { name: 'channels',     label: t('nav.channels'),     icon: 'i-lucide-satellite-dish', permission: 'channel:read', module: 'distribution' },
        { name: 'facilities',   label: t('nav.facilities'),   icon: 'i-lucide-waves',         permission: 'facility:read', module: 'groups_events' },
        // The events sold into those halls (docs/DOGODKI.md §3.1). `event:read` and not
        // `facility:read`: the wellness desk holds the second and two therapists have it,
        // and a banqueting contract for 14 000 € is not the same act. Same licence key as
        // the halls and the groups — MICE is one story and one module.
        { name: 'events',       label: t('nav.events'),       icon: 'i-lucide-party-popper',  permission: 'event:read', module: 'groups_events' },
        { name: 'dining',       label: t('nav.dining'),       icon: 'i-lucide-utensils',      permission: 'dining:read' },
        { name: 'maintenance',  label: t('nav.maintenance'),  icon: 'i-lucide-wrench',        permission: 'maintenance:read', module: 'operations' },
        // The morning of the floor, read-only, beside the other operational registers.
        // `housekeeping:read` is the fence and it is the right one: a receptionist holds
        // no housekeeping key at all (docs/PRAVICE.md §5), so the entry appears for the
        // head of reception and for management — which is who asked for the screen.
        { name: 'housekeeping-overview', label: t('nav.housekeeping_overview'), icon: 'i-lucide-brush-cleaning', permission: 'housekeeping:read', module: 'operations' },
        { name: 'lost-items',   label: t('nav.lost_items'),   icon: 'i-lucide-package-search', permission: 'housekeeping:read', module: 'operations' },
        // The cars the desk arranges. Beside the other operational registers and keyed
        // by reservation:read, which is what the route behind it asks for.
        { name: 'transfers',    label: t('nav.transfers'),    icon: 'i-lucide-car-front',     permission: 'reservation:read' },
        // Finance carries the invoices as its Računi tab, so the entry shows for
        // either key; reports sit under the money, where their reader goes next.
        { name: 'finance',      label: t('nav.finance'),      icon: 'i-lucide-wallet',        permission: 'finance:read', anyOf: ['finance:read', 'invoice:read'] },
        { name: 'reports',      label: t('nav.reports'),      icon: 'i-lucide-chart-column',  permission: 'report:read', module: 'reports' },
        // "AI pomočnik" was here. It is Milo, the orb in the top bar, now — a thing
        // you talk to over the screen you are on, not a screen you leave the
        // work to go to. The ROUTE stays (/pomocnik) for long work and for the
        // supervisor's "Vsi pogovori", reached from the drawer and by address; a menu
        // entry beside it would be two doors to one thing, one of which loses the page
        // the question was about.
    ]))

    // Everything about the relationship with a guest or a firm, under one heading:
    // the overview reads the rest, so it leads; guests and companies are the two
    // subjects; segments, the funnel and the to-do list act on them.
    const crmItems = computed((): NavItem[] => allow([
        { name: 'crm-dashboard',     label: t('nav.crm_overview'),  icon: 'i-lucide-layout-dashboard', permission: 'guest:read' },
        { name: 'crm-manager',       label: t('nav.crm_manager'),   icon: 'i-lucide-chart-line',       permission: 'crm:read' },
        { name: 'guests',            label: t('nav.guests'),        icon: 'i-lucide-users',            permission: 'guest:read' },
        { name: 'companies',         label: t('nav.companies'),     icon: 'i-lucide-briefcase',        permission: 'guest:read' },
        { name: 'crm-segments',      label: t('nav.segments'),      icon: 'i-lucide-filter',           permission: 'guest:read' },
        { name: 'crm-opportunities', label: t('nav.opportunities'), icon: 'i-lucide-trending-up',      permission: 'guest:read' },
        { name: 'crm-tasks',         label: t('nav.tasks'),         icon: 'i-lucide-check-check',      permission: 'guest:read' },
    ]))

    // The commercial fold: everything about selling the house before a booking
    // exists — the offers today, more to come. Same shape as the CRM fold.
    const salesItems = computed((): NavItem[] => allow([
        { name: 'offers', label: t('nav.offers'), icon: 'i-lucide-sparkle', permission: 'reservation:read' },
    ]))

    // The materials fold: Imprion Materials, the module (docs/MATERIALNO.md §10).
    //
    // Every entry carries `module: 'materials'`, so a house that has not bought it
    // draws no heading at all — the fold hides itself when the list comes back empty.
    // The licence is asked and not only the permission, unlike the staff fold above:
    // `stock:read` and `retail:read` are deliberately NOT masked when the module is off
    // (pkg/enums/Module, permissionOwner), because the housekeeping portal reads a
    // trolley with the first of them and posts a minibar bottle with `stock:move`. So
    // here the licence is the whole of the fence, and a permission alone would draw a
    // menu into three screens the server refuses.
    /** How many requisitions wait for this account; see loadPendingApprovals below. */
    const pendingApprovals = ref(0)

    /**
     * # Why five lists and not one with a `group` field
     *
     * The same reason the staff fold below gives: `allow()` filters, and a group whose
     * every entry was filtered out must not leave a heading behind. Five lists, each
     * filtered, and a group with nothing in it is dropped by `materialsGroups`.
     *
     * Nineteen entries in one flat fold was the state until now, and nineteen is past
     * what anybody reads down: „Prevzem“ sat between „Odobritve“ and „Inventure“
     * because that is the order they were built in, and a storekeeper looking for the
     * quarantine read the whole list every time. The headings are the order the work
     * is done in — what is on the shelf, what the house buys, what arrives, what it is
     * charged for, and the chain around it.
     *
     * # The rule for every entry below
     *
     * **The entry needs every right the screen's first read needs.** A menu drawn on the
     * key that names the screen — „the till is `retail:sell`“ — is a door onto a page
     * whose opening calls are refused, and what the person sees is not a refusal but an
     * empty screen. Where a screen opens on more than one read, the entry carries
     * `allOf` and the route carries the same list in `meta.permissions`, and the two are
     * changed together.
     */

    /** Zaloga — what is on the shelf now, and the two screens that move it over a counter. */
    const materialsStockItems = computed((): NavItem[] => allow([
        { name: 'stock',           label: t('nav.stock'),            icon: 'i-lucide-boxes',           permission: 'stock:read',      module: 'materials' },
        // The supervisor's own screen: `stock:inventory` is the right that starts a
        // stocktake and signs off its posting (docs/MATERIALNO-MOBILNO.md, WP-M7).
        { name: 'materials-counts', label: t('nav.materials_counts'), icon: 'i-lucide-clipboard-check', permission: 'stock:inventory', module: 'materials' },
        // `retail:sell` and NOT `retail:read` alone: this entry opens the COUNTER, and
        // the counter is a keypad, a shift and a drawer — every one of which the screen
        // itself refuses without `retail:sell` (`views/retail/Retail.vue`, `canSell`).
        // On `retail:read` the menu drew a till for people who could only read one, and
        // pressing it landed them on a page that told them so. What `retail:read` really
        // opens — the tills and their shift history — is its own entry under Settings
        // and keeps that key alone.
        //
        // All three, by the rule above: the screen's opening reads are `/retail/tills`
        // (`retail:read`), `/stock/items`, `/stock/places` and `/stock/balances`
        // (`stock:read`) and `/retail/rooms` plus the shift (`retail:sell`). A seller
        // who may not read the shelf gets a till with nothing on it.
        {
            name: 'retail', label: t('nav.retail'), icon: 'i-lucide-scan-barcode',
            permission: 'retail:sell', allOf: ['retail:sell', 'retail:read', 'stock:read'], module: 'materials',
        },
    ]))

    /** Nabava — everything between deciding to buy something and being allowed to. */
    const materialsProcurementItems = computed((): NavItem[] => allow([
        { name: 'suppliers', label: t('nav.suppliers'), icon: 'i-lucide-building-2', permission: 'stock:read', module: 'materials' },
        // The buyer's own door (docs/MATERIALNO-KATALOG.md, WP-M3a) — the supplier's
        // catalogue and the contracts behind its prices. `materials:procure` and NOT
        // `stock:read`: what the house AGREES TO PAY is not the same authority as
        // reading a shelf, and the mask takes the right away with the licence, so a
        // house without the module has nothing to draw this entry from either.
        { name: 'materials-catalogue', label: t('nav.materials_catalogue'), icon: 'i-lucide-book-marked', permission: 'materials:procure', module: 'materials' },
        // The buying half (docs/MATERIALNO-NAROCILA.md, WP-M3b), on `materials:procure`
        // rather than `stock:read`: approving an order is the house committing to spend
        // and sending one is a letter that leaves the building with the hotel's name on
        // it, which is not the authority that reads a trolley.
        { name: 'materials-orders', label: t('nav.materials_orders'), icon: 'i-lucide-clipboard-list', permission: 'materials:procure', module: 'materials' },
        { name: 'materials-rfqs',   label: t('nav.materials_rfqs'),   icon: 'i-lucide-mails',          permission: 'materials:procure', module: 'materials' },
        // The asking half (docs/MATERIALNO-ZAHTEVKI.md, WP-M4). The register is opened
        // by EITHER key: a chef raises requisitions and never approves one, a director
        // approves them and never writes one, and both need the same list. „Moje
        // odobritve“ and the rules behind it are the approver's alone — the second is a
        // screen that decides how the house's money is committed.
        {
            name: 'materials-requisitions', label: t('nav.materials_requisitions'), icon: 'i-lucide-file-input',
            permission: 'materials:requisition', anyOf: ['materials:requisition', 'materials:approve'], module: 'materials',
        },
        {
            name: 'materials-approvals', label: t('nav.materials_approvals'), icon: 'i-lucide-stamp',
            permission: 'materials:approve', module: 'materials', badge: pendingApprovals.value,
        },
        // The rules are their OWN key. Approving a requisition is answering the
        // question the house has already decided; writing the rules is deciding it —
        // from what amount, by whom, and what happens when the deadline passes. An
        // approver who may also rewrite the ladder can approve anything by first
        // lowering the rung, which is the one thing the ladder exists to stop.
        {
            name: 'materials-approval-rules', label: t('nav.materials_approval_rules'), icon: 'i-lucide-list-checks',
            permission: 'materials:rules', module: 'materials',
        },
    ]))

    /**
     * Prevzem — the goods arriving, and what goes back.
     *
     * Pravica je skladiščnikova (`stock:*`), modul je Materialno — hiša brez modula nima
     * karantene in nima česa vrniti. „Mobilno“ stands on `stock:read`, the lowest of the
     * three flows behind it: the hub draws only the cards the account may walk into, and
     * offering the door to a counter who may not post a receipt is right.
     */
    const materialsReceivingItems = computed((): NavItem[] => allow([
        { name: 'materials-receiving-issues', label: t('nav.materials_receiving'), icon: 'i-lucide-package-search', permission: 'stock:read', module: 'materials' },
        { name: 'materials-returns', label: t('nav.materials_returns'), icon: 'i-lucide-undo-2', permission: 'stock:read', module: 'materials' },
        { name: 'materials-mobile', label: t('nav.materials_mobile'), icon: 'i-lucide-smartphone', permission: 'stock:read', module: 'materials' },
    ]))

    /** Računi in knjigovodstvo — what the house is charged, and where it books it. */
    const materialsAccountingItems = computed((): NavItem[] => allow([
        // The book-keeper's own door, on `materials:invoice` rather than `stock:read`
        // (docs/MATERIALNO.md §10.8). Unlike its neighbours in Zaloga that right IS
        // masked when the licence lapses (pkg/enums/Module, permissionOwner), so the
        // entry disappears with the module rather than only being refused behind it.
        // `stock:read` beside it, by the rule above: the register reads the supplier
        // codebook FIRST and every row's name comes out of it. The screen no longer
        // stops on a refused supplier read — the names go blank and the invoices still
        // arrive — but an account that may read no supplier at all has no business in
        // a list whose every row would be nameless.
        {
            name: 'materials-invoices', label: t('nav.materials_invoices'), icon: 'i-lucide-file-text',
            permission: 'materials:invoice', allOf: ['materials:invoice', 'stock:read'], module: 'materials',
        },
        // The accounting half (docs/MATERIALNO-KNJIGOVODSTVO.md). Its own right —
        // `materials:accounting` — and NOT `stock:read`, because a chart of accounts and
        // a button that writes into the company's books are not the same authority as
        // reading a shelf. The mask takes the right away with the licence, so a house
        // without the module has nothing to draw this entry from either.
        { name: 'materials-accounting', label: t('nav.materials_accounting'), icon: 'i-lucide-book-open', permission: 'materials:accounting', module: 'materials' },
        // The planning half (WP-M6, docs/MATERIALNO-NAPOVED-ZASLONI.md). The forecast is
        // the BUYER's screen — it suggests what to order and writes a requisition — so it
        // stands on `materials:procure`; the budgets are the chart of what the house meant
        // to spend and stand on `materials:accounting`, the same key as the books beside
        // them. They sit under one heading and are still two entries, because the two are
        // opened by two different people on two different mornings.
        { name: 'materials-budgets', label: t('nav.materials_budgets'), icon: 'i-lucide-wallet', permission: 'materials:accounting', module: 'materials' },
        { name: 'materials-forecast', label: t('nav.materials_forecast'), icon: 'i-lucide-trending-up', permission: 'materials:procure', module: 'materials' },
        // The management half (docs/MATERIALNO-KAZALNIKI.md,
        // docs/MATERIALNO-ZAKLJUCEK.md). The indicators stand on `anyOf`, because the
        // route is a guardAny over the accounting key and `report:read`: a general
        // manager answers for what a room-night costs and holds the second of those, and
        // an entry on the accounting key alone would hide the screen from the person it
        // was written for. The close is the accounting key alone — pressing it stops
        // everybody else posting into the month.
        { name: 'materials-kpi', label: t('nav.materials_kpi'), icon: 'i-lucide-gauge', permission: 'materials:accounting', anyOf: ['materials:accounting', 'report:read'], module: 'materials' },
        { name: 'materials-period-close', label: t('nav.materials_period_close'), icon: 'i-lucide-calendar-check', permission: 'materials:accounting', module: 'materials' },
    ]))

    /**
     * Veriga — buying as a group, and stock moving between houses (WP-M5,
     * docs/VERIGE-ZASLONI.md).
     *
     * Both carry the `chains` module and not `materials`: the licence being asked about
     * is the chain one, and a house that bought Materials alone has no chain to draw —
     * so this heading disappears on its own for every single hotel.
     */
    const materialsChainItems = computed((): NavItem[] => allow([
        // The RIGHTS are the ordinary two — what the chain agrees to pay is the buyer's
        // authority (`materials:procure`), and stock that leaves one shelf for another
        // house is the storekeeper's (`stock:post`).
        // `chains:read` beside the buyer's key, by the rule above: the screen's first
        // call is `GET /properties/{id}/chain/`, and without that right the page drew
        // „ta hiša ni v verigi“ — a house being told it has no chain because it may not
        // read one.
        {
            name: 'materials-chain', label: t('nav.materials_chain'), icon: 'i-lucide-network',
            permission: 'materials:procure', allOf: ['chains:read', 'materials:procure'], module: 'chains',
        },
        // The transfers stand on BOTH licences and say so: a house with Materials and no
        // chain has nowhere to send anything, and one with a chain and no Materials has
        // no shelf for the goods to leave. `stock:post` is deliberately outside the
        // `materials:*` family the licence masks, so without both names here the entry
        // would survive a missing Materials module.
        { name: 'materials-transfers', label: t('nav.materials_transfers'), icon: 'i-lucide-truck', permission: 'stock:post', modules: ['materials', 'chains'] },
    ]))

    /**
     * „Pregled“ — the one screen that answers „kaj me čaka“ across all five groups
     * (docs/MATERIALNO.md §10.11). The TODO that stood here is closed: the route exists.
     *
     * `anyOf` and not one key, because the screen is panels and each panel has its own:
     * a storekeeper, a buyer, an approver, a book-keeper and a director all belong here
     * and no two of them hold the same thing. `permission` is the first of the five and
     * is never consulted while `anyOf` is set (see `allow` above) — it is there because
     * every NavItem carries one.
     *
     * `report:read` is deliberately NOT in the list although the director's panel opens
     * on it. It is not a `materials:*` key, so the licence mask does not take it away,
     * and an entry standing on it alone would survive into the menu of a house that
     * never bought the module.
     */
    const materialsOverviewItems = computed((): NavItem[] => allow([
        {
            name: 'materials-overview', label: t('nav.materials_overview'), icon: 'i-lucide-list-todo',
            permission: 'stock:read', module: 'materials',
            anyOf: ['stock:read', 'materials:procure', 'materials:approve', 'materials:accounting'],
        },
    ]))

    /**
     * The fold's groups, minus any that this account has nothing in.
     *
     * „Pregled“ is a group of its own with no heading, at the top: it is the front door
     * of the fold rather than a member of one of the five, and a heading over a single
     * entry repeating its own word would be furniture. The empty label is what keeps the
     * heading away — see the template.
     */
    const materialsGroups = computed((): { key: string, label: string, items: NavItem[] }[] => [
        { key: 'overview',   label: '',                                   items: materialsOverviewItems.value },
        { key: 'stock',      label: t('nav.materials_group_stock'),      items: materialsStockItems.value },
        { key: 'purchasing', label: t('nav.materials_group_purchasing'), items: materialsProcurementItems.value },
        { key: 'receiving',  label: t('nav.materials_group_receiving'),  items: materialsReceivingItems.value },
        { key: 'accounting', label: t('nav.materials_group_accounting'), items: materialsAccountingItems.value },
        { key: 'chain',      label: t('nav.materials_group_chain'),      items: materialsChainItems.value },
    ].filter(group => group.items.length))

    /** Every materials entry, for the fold's own open/active arithmetic. */
    const materialsItems = computed((): NavItem[] => materialsGroups.value.flatMap(group => group.items))

    /**
     * The staff fold, in three groups (docs/OSEBJE-UI.md §2). Each entry names the keys
     * its own route is guarded by, so nothing here offers a screen that would answer 403:
     *
     *   - the rota is read by `staff:read` and written by `staff:schedule`;
     *   - the personnel register is `staff:manage` alone — a card is personal data
     *     that reception has no reason to see, and it deliberately does not hide under
     *     `user:read`;
     *   - coverage is `staff:read` or the director's read-only `staff:oversee`;
     *   - the balances are `staff:time`, because a balance is a reading of the record.
     *
     * Each also names its module. The masking alone would very nearly do it — a house
     * without the licence has those permissions dropped by the API, so the fold would
     * mostly disappear on its own — but "very nearly" is what this entry is about: the
     * portal now reads the licence itself (docs/MODULI.md §3.3), so the fold is drawn
     * from the licence rather than from a side effect of it, and the one entry the
     * masking could never reach is fenced properly at last (see `staff-me`).
     *
     * # Why three lists and not one with a `group` field
     *
     * `allow()` filters, and a group whose every entry was filtered out must not leave a
     * heading behind. Three lists, each filtered, and a group with nothing in it is
     * dropped by `staffGroups` — which a single list with a tag on each row would have
     * had to work out afterwards anyway.
     */

    /** Osebno — one page, about the person reading it. No permission; §13 says why. */
    const staffPersonalItems = computed((): NavItem[] => allow([
        {
            // „Jaz“ (docs/OSEBJE-UI.md §3). The ROUTE needs no permission and the API
            // checks none — it narrows to the signed-in account instead — so the module
            // is the real fence, and the fold's own keys are what keep a staff page in a
            // staff fold. When `staff-web` exists it is that portal's own page and needs
            // no fence at all.
            //
            // **And no permission here either, which is the whole point of the page.**
            // It used to ask for one of six staff keys, and every one of them is a
            // MANAGER's key: a housekeeper, a waiter, a night porter — everybody the
            // record is actually about — was shown no entry at all, and „Jaz“ is where
            // they clock in, read their own hours, ask for a correction and open their
            // own contract. docs/OSEBJE.md §13 is explicit that all of that is
            // self-service and needs no permission, and the comment above was already
            // saying so while the entry below contradicted it.
            //
            // The module fence stays and is the only one: a hotel without the staff
            // licence draws no staff fold, and inside a house that has it the page is
            // every signed-in employee's own.
            name:       'staff-me',
            label:      t('nav.me'),
            icon:       'i-lucide-user-round',
            permission: '',
            module:     'staff',
        },
    ] as NavItem[]))

    /**
     * Vodenje — the week, in the order it is worked: make it, check it holds, decide
     * what moves it, then read what it came to.
     *
     * „Danes“ stands BEFORE the week, and it is the one entry here that is not part of
     * that order. It is what the person on duty opens at seven in the morning — the six
     * things about this day that somebody has to do something about (docs/OSEBJE-UI.md,
     * „Danes“) — and everything under it is the week. A screen answering „what is wrong
     * right now“ filed after five screens about the month is a screen nobody reaches
     * before the morning it was about is over.
     */
    const staffManagementItems = computed((): NavItem[] => allow([
        {
            // The same four keys the read carries. Any one of them is somebody who
            // leads part of this house, and the answer is narrowed to their own
            // departments by the server — the same scope the rota grid is drawn with
            // (docs/OSEBJE.md §13).
            name:       'staff-today',
            label:      t('nav.staff_today'),
            icon:       'i-lucide-sunrise',
            permission: 'staff:schedule',
            anyOf:      ['staff:schedule', 'staff:oversee', 'staff:manage', 'staff:approve'],
            module:     'staff',
        },
        {
            name:       'staff-rota',
            label:      t('nav.rota'),
            icon:       'i-lucide-calendar-days',
            permission: 'staff:read',
            anyOf:      ['staff:schedule', 'staff:read'],
            module:     'staff',
        },
        {
            name:       'staff-coverage',
            label:      t('nav.coverage'),
            icon:       'i-lucide-gauge',
            permission: 'staff:read',
            anyOf:      ['staff:read', 'staff:oversee'],
            module:     'staff',
        },
        {
            // Deciding leave and entering days into the record. `staff:approve` and not
            // `staff:time`: looking at the record and moving it are different rights
            // (docs/OSEBJE.md §13), and the same key already decides a correction.
            name:       'staff-absences',
            label:      t('nav.absences'),
            icon:       'i-lucide-calendar-off',
            permission: 'staff:approve',
            module:     'staff',
        },
        {
            // Deciding corrections. `staff:approve` and not `staff:time`: looking at
            // the record and moving it are different rights (docs/OSEBJE.md §13).
            name:       'staff-time-corrections',
            label:      t('nav.time_corrections'),
            icon:       'i-lucide-pencil-line',
            permission: 'staff:approve',
            module:     'staff',
        },
        {
            // The house's record. `staff:time`, which is the key the API guards the
            // read with — an entry leading to a screen that answers 403 is a lie.
            name:       'staff-time',
            label:      t('nav.time_record'),
            icon:       'i-lucide-calendar-clock',
            permission: 'staff:time',
            module:     'staff',
        },
        {
            // The year's leave, per person. Same key as the record beside it and for the
            // same reason: a balance is a reading of the record. Issuing a decision needs
            // `staff:manage`, which the screen gates rather than the menu — somebody with
            // `staff:time` alone has a real screen to read here.
            name:       'staff-vacations',
            label:      t('nav.vacations'),
            icon:       'i-lucide-palmtree',
            permission: 'staff:time',
            module:     'staff',
        },
    ] as NavItem[]))

    /** Kadri — the cards, and the registers the cards name. `staff:manage` on both. */
    const staffHRItems = computed((): NavItem[] => allow([
        {
            name:       'staff-people',
            label:      t('nav.employments'),
            icon:       'i-lucide-id-card',
            permission: 'staff:manage',
            module:     'staff',
        },
        {
            // The departments and the posts. `staff:manage` and not a register key of
            // its own: somebody who may not file a work period has no business inventing
            // the department it would name (docs/OSEBJE.md §5).
            name:       'staff-catalogue',
            label:      t('nav.staff_catalogue'),
            icon:       'i-lucide-list-tree',
            permission: 'staff:manage',
            module:     'staff',
        },
        {
            // The same two registers seen as the house's own structure (§17). It reads
            // with `staff:read` and the drag on it asks for `staff:manage`, which the
            // page itself gates — so the entry sits beside the register it is derived
            // from and is offered to whoever may read the organisation.
            name:       'staff-org-chart',
            label:      t('nav.staff_org_chart'),
            icon:       'i-lucide-network',
            permission: 'staff:read',
            module:     'staff',
        },
    ] as NavItem[]))

    /** The fold's three groups, minus any that this account has nothing in. */
    const staffGroups = computed((): { key: string, label: string, items: NavItem[] }[] => [
        { key: 'personal',   label: t('nav.staff_personal'),   items: staffPersonalItems.value },
        { key: 'management', label: t('nav.staff_management'), items: staffManagementItems.value },
        { key: 'hr',         label: t('nav.staff_hr'),         items: staffHRItems.value },
    ].filter(group => group.items.length))

    /** Every staff entry, for the fold's own open/active arithmetic. */
    const staffItems = computed((): NavItem[] => staffGroups.value.flatMap(group => group.items))

    /**
     * Routes that belong to a menu entry without carrying its name.
     *
     * Written out rather than derived from a prefix, because a rule like "any route whose
     * name begins with this one" would quietly claim entries nobody meant it to — and the
     * cost of getting it wrong is a menu that highlights the wrong screen, which nobody
     * reports and everybody misreads.
     *
     * There are two. A single group is drawn on the groups screen (route `group`,
     * /skupine/{id}), and arriving there from a block on the chessboard must not leave
     * the menu looking as though nothing is open. A person's card (`staff-person`,
     * /staff/people/{userId}) is the register opened at one row, and belongs to
     * „Kadrovske kartice“ for exactly the same reason.
     */
    const NESTED_ROUTES: Record<string, string> = {
        group:          'groups',
        'staff-person': 'staff-people',
    }

    const isActive = (name: string): boolean =>
        route.name === name
        || NESTED_ROUTES[String(route.name ?? '')] === name
        || route.path.startsWith(`/${name}`)

    /** The CRM fold is active for its own screens and every /crm/* profile route. */
    const crmActive = (): boolean =>
        crmItems.value.some(item => isActive(item.name)) || route.path.startsWith('/crm')

    /**
     * What lives under Settings: the house, the accounts, and this browser's look.
     *
     * Properties is here rather than in the list above because it is set up once — a
     * hotel adds a room type in the spring and does not touch it again — while
     * everything above is opened while a guest is standing at the desk.
     */
    const settingsItems = computed((): NavItem[] => allow([
        { name: 'properties',         label: t('nav.properties'), icon: 'i-lucide-building-2', permission: 'property:read' },
        { name: 'settings-users',     label: t('nav.users'),      icon: 'i-lucide-users',      permission: 'user:read', tenantWide: true },
        // The roles themselves, on their own screen. `user:manage` and not `user:read`:
        // the page exists to CHANGE what a role allows, and an entry that leads to a
        // screen somebody can only look at is an entry that answers the wrong question
        // — the read-only view of the same bundles is already on the accounts screen.
        { name: 'settings-roles',     label: t('nav.roles'),      icon: 'i-lucide-shield-check', permission: 'user:manage', tenantWide: true },
        // Which houses buy as one (WP-M5). `chains:manage` and not `chains:read`: the
        // page exists to CHANGE the chain, and reading who is in one is on „Materialno ›
        // Veriga“, beside the prices it decides. `tenantWide`, because `/chains` is
        // outside `/properties` — an account assigned to a single house resolves no
        // permission there, and an entry that leads to a 403 is worse than no entry.
        { name: 'settings-chains', label: t('chain.nav'), icon: 'i-lucide-network', permission: 'chains:manage', module: 'chains', tenantWide: true },
        { name: 'settings-tills',     label: t('retail.tills'),   icon: 'i-lucide-store',      permission: 'retail:read' },
        { name: 'settings-dining',    label: t('nav.dining'),     icon: 'i-lucide-utensils',   permission: 'dining:manage' },
        // The municipality's rate card and the grounds for not paying it
        // (docs/ETURIZEM.md §6). `compliance:read`, matching the route's own guard: the
        // rates and the relief list are what the house files with the state, and the
        // same key opens the registrations screen beside a reservation.
        { name: 'settings-tourist-tax', label: t('tourist_tax.nav'), icon: 'i-lucide-landmark', permission: 'compliance:read' },
        // property:write, because what is on this screen is the house's own equipment:
        // which machines may talk to its data, which is not a housekeeping decision.
        { name: 'settings-housekeeping', label: t('nav.housekeeping'), icon: 'i-lucide-brush-cleaning', permission: 'property:write', module: 'operations' },
        // property:write for the same reason: a screen on a wall is the house's own
        // equipment, and which machines may talk to its data is its decision. One
        // entry for the room televisions AND the foyer panels since 1.494.1 — two
        // entries with the word "screens" in both was one too many for the person who
        // hangs them (docs/DISPLAY.md §10).
        { name: 'settings-screens', label: t('screens.nav'), icon: 'i-lucide-tv-minimal', permission: 'property:write' },
        // property:write like the mail settings: it holds the tokens that post as the
        // hotel, so reading the screen is already a privilege. `marketing` since
        // 2026-09-03, alongside the house's own website (docs/MODULI.md).
        { name: 'settings-publishing', label: t('publishing.nav'), icon: 'i-lucide-megaphone', permission: 'property:write', module: 'marketing' },
        // How the house reaches its own people (docs/OBVESCANJE.md). Beside Objave
        // because both are credential screens for something that speaks as the hotel,
        // and property:write for the same reason. NO module: the transport is
        // house-wide, and the rota is only the first thing to use it.
        { name: 'settings-notifications', label: t('notifications.nav'), icon: 'i-lucide-bell-ring', permission: 'property:write' },
        // The house's revision trail (docs/REVIZIJSKA_SLED.md). `audit:read` and
        // deliberately not property:write, which every credential screen above it uses:
        // the trail names who did what minute by minute, INCLUDING the person reading it,
        // and "may configure the hotel" is not the same authority as "may read
        // everybody's day". Migration 157 gives it to Skrbnik and Vodja recepcije.
        { name: 'settings-audit', label: t('audit.nav'), icon: 'i-lucide-scroll-text', permission: 'audit:read' },
        // Two keys, matching the route's own guard (HTTP_API/AIUsage.go): the
        // accountant who will check the surcharge, and the person who switched the
        // paid feature on. Either is owed the sight of what it costs.
        {
            name:       'settings-ai-usage',
            label:      t('ai_usage.nav'),
            icon:       'i-lucide-gauge',
            permission: 'finance:read',
            anyOf:      ['finance:read', 'property:write'],
        },
        // Whether the house's assistant answers a guest, and what he may prepare for
        // them (plan §11a). property:write, matching the route it writes through, and
        // `guest` because it is a switch on the guest portal — a house without that
        // module has no portal for Milo to stand in.
        { name: 'settings-milo', label: t('milo_settings.nav'), icon: 'i-lucide-message-circle-heart', permission: 'property:write', module: 'guest' },
        // What the guest portal LOOKS like — the name, the line under it, the colours,
        // the corners (docs/PLAN_GOST.md §A2). Beside Milo because both are decisions
        // about the same portal, and `guest` for the same reason: a house without the
        // portal has nothing to dress.
        //
        // property:READ and not write, which is where it parts from Milo and from the
        // credential screens above. Nothing on it is a secret — it is the hotel's own
        // name and two colours, printed on a page any guest of the house can open — so
        // hiding the menu entry from somebody who may read the property would hide the
        // ANSWER to "what does our portal say", which is the question this screen was
        // built for. Saving still asks for property:write, on the card and on the route.
        { name: 'settings-guest-portal', label: t('settings_guest_portal.nav'), icon: 'i-lucide-palette', permission: 'property:read', module: 'guest' },
        // Whether the assistants may answer how busy the town is, and the TomTom key
        // they read the road through (docs/AI.md §29). property:write like every other
        // credential screen, and deliberately NO module: the reading is shared by the
        // desk's own Milo and the guest portal's, so a house without the portal must
        // still be able to configure it.
        { name: 'settings-city-pulse', label: t('city_pulse.nav'), icon: 'i-lucide-activity', permission: 'property:write' },
        // The ordering PIN the guest's phone and the room TV ask for (mirror of navManifest).
        { name: 'settings-order-pin', label: t('order_pin.nav'), icon: 'i-lucide-key-round', permission: 'property:write' },
        // Which system reads this house's rooms, and where its equipment stands.
        // property:write for the same reason the screens on the walls have it: it is the
        // house's own equipment, and which machines may talk to its data is its decision.
        // The AJPES account the business register is read on (docs/PODJETJA.md § 10).
        // property:write like the other credential screens, and for the sharper reason:
        // what is on this page is bought by the unit and spent by the question.
        { name: 'settings-business-register', label: t('business_register.nav'), icon: 'i-lucide-library', permission: 'property:write' },
        { name: 'settings-grms', label: t('grms.nav'), icon: 'i-lucide-cpu', permission: 'property:write' },
        // The demonstration console, and the one entry in this menu that a permission
        // alone does not decide. It exists ONLY where the house runs simulated
        // equipment — a hotel with controllers in its rooms has no such screen at all,
        // because a console offering to open a window in an occupied room is a console
        // somebody eventually presses (docs/SOBNA-TELEMETRIJA.md §4.7.8b). The house is
        // asked below; until it has answered the entry stays out.
        {
            name:       'settings-demo',
            label:      t('demo.nav'),
            icon:       'i-lucide-flask-conical',
            permission: 'telemetry:automation',
            when:       appStore.demoConsole,
        },
        // Everything a house decides once about its people, on one tabbed page: the
        // working-time record and the corridor tablets, the holiday calendar, the leave
        // rules and the document templates (docs/OSEBJE-UI.md §2). The registers that
        // used to sit beside it are in the staff fold now, with the cards that name them.
        //
        // `staff:manage`: somebody who may not file a work period has no business
        // deciding whether the house stamps breaks, or what leave is made of.
        {
            name:       'settings-staff',
            label:      t('nav.staff_group'),
            icon:       'i-lucide-users-round',
            permission: 'staff:manage',
            module:     'staff',
        },
        // Everybody may change their own language and their own theme.
        { name: 'settings-appearance', label: t('nav.appearance'), icon: 'i-lucide-palette',   permission: '' },
    ] as NavItem[]))

    /**
     * Whether this house has a demonstration console, asked once per house.
     *
     * The service answers with an empty scenario list unless its driver is the
     * simulator AND this house has it switched on, so one call decides the entry. It is
     * asked only where the account could open the screen anyway — a receptionist's
     * browser must not be sending requests to an endpoint that would refuse it.
     *
     * A failed call leaves the entry out, which is the safe way round: the worst case
     * is a presenter who has to reload, not a hotel with a button that opens windows.
     */
    /**
     * How many requisitions are waiting for THIS account to answer
     * (docs/MATERIALNO-ZAHTEVKI.md, WP-M4).
     *
     * Read once when the menu is drawn, and again only when the house or the
     * permissions change — never polled. A number in the corner of the eye that moves
     * on its own is a notification, and this portal has a bell for those; what this is
     * for is the person who signs in at eight and needs to know there is something to
     * sign before they go looking for it.
     *
     * A failed call leaves the badge off, which is the safe way round: the worst case
     * is an approver who opens the screen anyway.
     */
    const loadPendingApprovals = async () => {
        if (!appStore.can('materials:approve') || !appStore.hasModule('materials') || !appStore.propertyId) {
            pendingApprovals.value = 0

            return
        }

        const { ok, requisitions } = await listMyApprovals(appStore.propertyId)

        pendingApprovals.value = ok ? requisitions.length : 0
    }

    const loadDemoConsole = async () => {
        if (!appStore.can('telemetry:automation') || !appStore.propertyId) {
            appStore.setDemoConsole(false)

            return
        }

        const { statusCode, data } = await fetchStore
            .fetch(`/properties/${appStore.propertyId}/telemetry/demo`)
            .get()
            .json<ITelemetryDemoConsole>()

        appStore.setDemoConsole(statusCode.value === 200 && (data.value?.scenarios?.length ?? 0) > 0)
    }

    const SALES_OPEN_KEY = 'pms:sales-open'
    const salesOpen = ref(false)

    const MATERIALS_OPEN_KEY = 'pms:materials-open'
    const materialsOpen = ref(false)

    const STAFF_OPEN_KEY = 'pms:staff-open'
    const staffOpen = ref(false)

    const toggleStaff = () => {
        // In the rail there is nothing to fold shut, so the press means "open this" —
        // and opening it means having the menu back first.
        staffOpen.value = collapsed.value || !staffOpen.value
        expandSidebar()
        localStorage.setItem(STAFF_OPEN_KEY, staffOpen.value ? '1' : '0')
    }

    const toggleSales = () => {
        // In the rail there is nothing to fold shut, so the press means "open this" —
        // and opening it means having the menu back first.
        salesOpen.value = collapsed.value || !salesOpen.value
        expandSidebar()
        localStorage.setItem(SALES_OPEN_KEY, salesOpen.value ? '1' : '0')
    }

    const toggleMaterials = () => {
        // In the rail there is nothing to fold shut, so the press means "open this" —
        // and opening it means having the menu back first.
        materialsOpen.value = collapsed.value || !materialsOpen.value
        expandSidebar()
        localStorage.setItem(MATERIALS_OPEN_KEY, materialsOpen.value ? '1' : '0')
    }

    const CRM_OPEN_KEY = 'pms:crm-open'

    /** Open when the screen you are on is inside it, and otherwise as it was left. */
    const crmOpen = ref(crmActive() || localStorage.getItem(CRM_OPEN_KEY) !== '0')

    salesOpen.value = salesItems.value.some(item => isActive(item.name)) || localStorage.getItem(SALES_OPEN_KEY) !== '0'
    materialsOpen.value = materialsItems.value.some(item => isActive(item.name)) || localStorage.getItem(MATERIALS_OPEN_KEY) !== '0'
    staffOpen.value = staffItems.value.some(item => isActive(item.name)) || localStorage.getItem(STAFF_OPEN_KEY) !== '0'


    const toggleCrm = () => {
        // In the rail there is nothing to fold shut, so the press means "open this" —
        // and opening it means having the menu back first.
        crmOpen.value = collapsed.value || !crmOpen.value
        expandSidebar()
        localStorage.setItem(CRM_OPEN_KEY, crmOpen.value ? '1' : '0')
    }

    const SETTINGS_OPEN_KEY = 'pms:settings-open'

    /** Open when the screen you are on is inside it, and otherwise as it was left. */
    const settingsOpen = ref(
        settingsItems.value.some(item => isActive(item.name))
        || localStorage.getItem(SETTINGS_OPEN_KEY) === '1',
    )

    const toggleSettings = () => {
        // In the rail there is nothing to fold shut, so the press means "open this" —
        // and opening it means having the menu back first.
        settingsOpen.value = collapsed.value || !settingsOpen.value
        expandSidebar()
        localStorage.setItem(SETTINGS_OPEN_KEY, settingsOpen.value ? '1' : '0')
    }

    // Arriving inside the group from anywhere else — a link, a redirect, the back
    // button — opens it, so the highlighted entry is never hidden behind a fold.
    watch(() => route.name, () => {
        if (settingsItems.value.some(item => isActive(item.name))) settingsOpen.value = true
        if (crmActive()) crmOpen.value = true
        if (staffItems.value.some(item => isActive(item.name))) staffOpen.value = true
    })

    // The permissions arrive after this menu is first drawn — a reload does not go
    // through the login screen, so the account is read in App.vue — and the house can be
    // switched at any time. Both are watched, because the answer depends on both.
    watch([() => appStore.propertyId, () => appStore.permissions], () => {
        void loadDemoConsole()
        void loadPendingApprovals()
    }, { immediate: true })

</script>
